import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X, Inbox, Loader2 } from "lucide-react";
import { useRouter } from "@/shared/lib/router/router";
import { useTranslation } from "react-i18next";
import { useNotifications } from "@/features/notification-center/model/NotificationContext";
import { NotificationItem } from "@/entities/notification/model/types";
import { openNotificationLink } from "@/features/notification-center/lib/openLink";
import { NotificationGroup, buildSections } from "../lib/notificationGroups";
import { GroupBlock, SingleRow } from "./NotificationRow";

interface NotificationPanelProps {
    isOpen: boolean;
    onClose: () => void;
}

export function NotificationPanel({ isOpen, onClose }: NotificationPanelProps) {
    const { t } = useTranslation("common");
    const {
        notifications,
        totalUnread,
        isLoading,
        totalPages,
        currentPage,
        markAsViewed,
        disableNotification,
        loadNextPage,
        fetchInbox,
    } = useNotifications();

    const router = useRouter();
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (isOpen) fetchInbox();
    }, [isOpen, fetchInbox]);

    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [isOpen, onClose]);

    const sections = useMemo(() => buildSections(notifications), [notifications]);

    const toggleGroup = useCallback((key: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const handleMarkRead = useCallback((id: string) => markAsViewed(id), [markAsViewed]);
    const handleHide = useCallback((id: string) => disableNotification(id), [disableNotification]);

    const handleOpenLink = useCallback(
        (item: NotificationItem) => {
            if (!item.link) return;
            if (!item.read) markAsViewed(item.id);
            onClose();
            openNotificationLink(item.link, router);
        },
        [markAsViewed, onClose, router]
    );

    const handleMarkGroupRead = useCallback(
        async (group: NotificationGroup) => {
            await Promise.all(group.items.filter((i) => !i.read).map((i) => markAsViewed(i.id)));
        },
        [markAsViewed]
    );

    const handleHideGroup = useCallback(
        async (group: NotificationGroup) => {
            await Promise.all(group.items.map((i) => disableNotification(i.id)));
        },
        [disableNotification]
    );

    const hasMore = currentPage + 1 < totalPages;

    return (
        <>
            <div
                onClick={onClose}
                className={`fixed inset-0 z-drawer_overlay bg-black/20 dark:bg-black/40 transition-opacity duration-slow ${
                    isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
                aria-hidden
            />

            <aside
                role="dialog"
                aria-modal="true"
                aria-label={t("nav.notifications.title")}
                className={`fixed top-0 right-0 bottom-0 z-drawer w-full sm:w-[400px] flex flex-col
                    bg-white dark:bg-night_surface border-l border-zinc-200 dark:border-white/10
                    transition-transform duration-slow ease-out
                    ${isOpen ? "translate-x-0" : "translate-x-full"}`}
            >
                <header className="flex items-center justify-between px-4 h-14 border-b border-zinc-200 dark:border-white/10 flex-shrink-0">
                    <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">
                        {t("nav.notifications.title")}
                        {totalUnread > 0 && (
                            <span className="ml-2 text-xs font-normal text-zinc-500 dark:text-white/50">
                                {t("nav.notifications.unreadCount", { count: totalUnread })}
                            </span>
                        )}
                    </h2>
                    <button
                        onClick={onClose}
                        className="w-7 h-7 flex items-center justify-center rounded text-zinc-500 dark:text-white/60 hover:bg-zinc-100 dark:hover:bg-white/10 transition-colors duration-fast"
                        aria-label={t("general.close")}
                    >
                        <X size={16} />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    {isLoading && notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-3">
                            <Loader2 size={20} className="animate-spin text-zinc-400 dark:text-white/40" />
                            <p className="text-xs text-zinc-500 dark:text-white/50">{t("nav.notifications.syncing")}</p>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-3 px-8 text-center">
                            <Inbox size={24} className="text-zinc-300 dark:text-white/25" />
                            <p className="text-sm font-medium text-zinc-700 dark:text-white/80">{t("nav.notifications.allClearTitle")}</p>
                            <p className="text-xs text-zinc-500 dark:text-white/50">{t("nav.notifications.allClearHint")}</p>
                        </div>
                    ) : (
                        sections.map((section) => (
                            <section key={section.bucket}>
                                <h3 className="sticky top-0 z-10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-white/50 bg-zinc-50 dark:bg-night_input border-b border-zinc-200 dark:border-white/10">
                                    {t(`nav.notifications.bucket.${section.bucket}`)}
                                </h3>
                                <div className="divide-y divide-zinc-100 dark:divide-white/5">
                                    {section.groups.map((group) =>
                                        group.items.length === 1 ? (
                                            <SingleRow
                                                key={group.key}
                                                item={group.latest}
                                                onMarkRead={handleMarkRead}
                                                onHide={handleHide}
                                                onOpenLink={handleOpenLink}
                                                t={t}
                                            />
                                        ) : (
                                            <GroupBlock
                                                key={group.key}
                                                group={group}
                                                expanded={expanded.has(group.key)}
                                                onToggle={() => toggleGroup(group.key)}
                                                onMarkRead={handleMarkRead}
                                                onHide={handleHide}
                                                onMarkGroupRead={() => handleMarkGroupRead(group)}
                                                onHideGroup={() => handleHideGroup(group)}
                                                onOpenLink={handleOpenLink}
                                                t={t}
                                            />
                                        )
                                    )}
                                </div>
                            </section>
                        ))
                    )}

                    {hasMore && (
                        <div className="px-4 py-3 flex justify-center">
                            <button
                                onClick={loadNextPage}
                                className="text-xs font-medium text-dark_green dark:text-emerald-400 hover:underline"
                            >
                                {t("nav.notifications.loadMore")}
                            </button>
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
}
